/**
 * Step 7 — Moisture Mapping & Water Assessment
 * Wraps existing MoistureMapping module for readings and adds:
 *  - Water Source & Category (Cat 1/2/3)
 *  - Materials matrix (Drywall / Flooring / etc) with dry-vs-remove
 *  - Equipment quantity calculator
 *  - Thermal imaging upload + AI analysis (manual upload path only)
 *  - Mark Complete / Mark N/A buttons
 */
import { useState, useRef } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Droplet, Loader2, Plus, Trash2, Calculator, Thermometer, X, Check, AlertCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import { AIResultNote } from "@/components/AIResultNote";
import { openPhotoPicker } from "@/lib/camera";

const WATER_SOURCES = ["Pipe Burst", "Appliance Leak", "Roof Leak", "Flooding", "Sewage Backup", "Unknown", "Other"];
const CATEGORIES = [
  { key: "Cat 1", label: "Cat 1 — Clean Water",  desc: "Originates from a sanitary source (e.g., broken supply line, faucet)." },
  { key: "Cat 2", label: "Cat 2 — Grey Water",    desc: "Significant contamination; may cause discomfort/illness if ingested (e.g., washing-machine overflow)." },
  { key: "Cat 3", label: "Cat 3 — Black Water",   desc: "Grossly unsanitary; contains pathogens or toxins (e.g., sewage backup, river flooding)." },
];
const MATERIALS = ["Drywall", "Flooring", "Cabinets", "Baseboards", "Ceiling", "Subfloor", "Other"];
const ASSESS_OPTS = ["Can Be Dried", "Requires Removal", "Undetermined"];

export default function Step7Moisture({ workflow, requestId, onAdvance, fetchWorkflow }) {
  const init = workflow?.step_7 || {};
  const [source, setSource] = useState(init.water_source || "");
  const [sourceOther, setSourceOther] = useState(init.water_source_other || "");
  const [category, setCategory] = useState(init.water_category || "");
  const [readings, setReadings] = useState(init.materials_readings || []);
  const [equipment, setEquipment] = useState({
    dehumidifiers: init.equipment_dehumidifiers || 0,
    air_movers: init.equipment_air_movers || 0,
    air_scrubbers: init.equipment_air_scrubbers || 0,
  });
  const [drying, setDrying] = useState({
    low: init.estimated_drying_days_low || 2,
    high: init.estimated_drying_days_high || 4,
  });
  const [notes, setNotes] = useState(init.notes || "");
  const [thermals, setThermals] = useState(init.thermal_images || []);
  const [sqft, setSqft] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploadingThermal, setUploadingThermal] = useState(false);
  const [naReason, setNaReason] = useState("");
  const [showNaDialog, setShowNaDialog] = useState(false);
  const thermalRef = useRef(null);

  const addReading = () => setReadings(r => [...r, {
    location: "", material: "Drywall", moisture_pct: 0, dry_standard_pct: 15, assessment: "Undetermined",
  }]);
  const removeReading = (i) => setReadings(r => r.filter((_, idx) => idx !== i));
  const updateReading = (i, patch) => setReadings(r => r.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const calcEquipment = async () => {
    try {
      const { data } = await API.post(`/inspection/${requestId}/step/7/equipment-calc`, null, {
        params: { affected_sqft: sqft, water_category: category || "Cat 1", material_count: readings.length || 1 },
      });
      setEquipment({
        dehumidifiers: data.dehumidifiers,
        air_movers: data.air_movers,
        air_scrubbers: data.air_scrubbers,
      });
      setDrying({ low: data.estimated_drying_days_low, high: data.estimated_drying_days_high });
      toast.success("Equipment & drying estimate calculated");
    } catch (err) {
      toast.error("Calc failed");
    }
  };

  const uploadThermal = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThermal(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("location", "");
    try {
      const { data } = await API.post(`/inspection/${requestId}/step/7/thermal-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      setThermals(prev => [...prev, data.image]);
      toast.success(data.image?.ai_analysis ? "Thermal image uploaded & analyzed by AI" : "Thermal image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingThermal(false);
      if (thermalRef.current) thermalRef.current.value = "";
    }
  };

  const acceptThermal = async (img) => {
    try {
      await API.post(`/inspection/${requestId}/step/7/thermal/${img.id}/accept`, { ai_analysis: img.ai_analysis });
      setThermals(prev => prev.map(t => t.id === img.id ? { ...t, accepted: true } : t));
    } catch (e) { toast.error("Could not accept"); }
  };

  const deleteThermal = async (id) => {
    try {
      await API.delete(`/inspection/${requestId}/step/7/thermal/${id}`);
      setThermals(prev => prev.filter(t => t.id !== id));
    } catch (e) { toast.error("Could not delete"); }
  };

  const save = async () => {
    if (!source) return toast.error("Pick a water source");
    if (source === "Other" && !sourceOther.trim()) return toast.error("Specify the source");
    if (!category) return toast.error("Pick a water category");
    if (readings.length === 0) return toast.error("Add at least one moisture reading");
    for (const r of readings) {
      if (!r.location.trim()) return toast.error("All readings need a location");
    }
    setSaving(true);
    try {
      await API.post(`/inspection/${requestId}/step/7/save`, {
        water_source: source,
        water_source_other: sourceOther || null,
        water_category: category,
        materials_readings: readings,
        equipment_dehumidifiers: equipment.dehumidifiers,
        equipment_air_movers: equipment.air_movers,
        equipment_air_scrubbers: equipment.air_scrubbers,
        estimated_drying_days_low: drying.low,
        estimated_drying_days_high: drying.high,
        notes,
      });
      toast.success("Moisture assessment saved");
      onAdvance(8);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const markNA = async () => {
    if (!naReason.trim()) return toast.error("Reason required for N/A");
    try {
      await API.put(`/inspection/${requestId}/step/7/status`, { status: "na", na_reason: naReason.trim() });
      toast.success("Step 7 marked Not Applicable");
      setShowNaDialog(false);
      fetchWorkflow();
      onAdvance(8);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark N/A");
    }
  };

  return (
    <div className="space-y-4" data-testid="step-7-form">
      <div className="flex items-center gap-2">
        <Droplet className="w-5 h-5 text-blue-500" />
        <h2 className="text-base font-bold text-zinc-900">Step 7 · Moisture & Water Assessment</h2>
      </div>

      {/* Water source */}
      <Card><CardContent className="pt-4 space-y-3">
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Water Source</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-11 mt-1" data-testid="step-7-source"><SelectValue placeholder="Select source" /></SelectTrigger>
            <SelectContent>{WATER_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {source === "Other" && (
            <Input value={sourceOther} onChange={e => setSourceOther(e.target.value)}
                   placeholder="Describe source" className="h-11 mt-2" data-testid="step-7-source-other" />
          )}
        </div>
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-2 block">Water Category</Label>
          <div className="space-y-2">
            {CATEGORIES.map(c => (
              <button key={c.key} onClick={() => setCategory(c.key)}
                      className={`w-full text-left p-3 rounded-lg border transition-all
                                  ${category === c.key ? "border-blue-500 bg-blue-50" : "border-zinc-200"}`}
                      data-testid={`step-7-cat-${c.key.toLowerCase().replace(" ", "-")}`}>
                <div className="flex items-center gap-2">
                  {category === c.key && <Check className="w-4 h-4 text-blue-600" />}
                  <p className="text-xs font-bold text-zinc-900">{c.label}</p>
                </div>
                <p className="text-[10px] text-zinc-600 mt-1">{c.desc}</p>
              </button>
            ))}
          </div>
        </div>
      </CardContent></Card>

      {/* Materials matrix */}
      <Card><CardContent className="pt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Moisture Readings</Label>
          <Button size="sm" variant="outline" onClick={addReading} data-testid="step-7-add-reading">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        {readings.length === 0 && <p className="text-xs text-zinc-400 italic">No readings yet.</p>}
        <div className="space-y-2">
          {readings.map((r, i) => (
            <div key={i} className="border border-zinc-200 rounded-lg p-2 space-y-2"
                 data-testid={`step-7-reading-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-bold text-zinc-500">READING #{i + 1}</p>
                <Button size="sm" variant="ghost" onClick={() => removeReading(i)} className="text-rose-500 -mr-2"
                        data-testid={`step-7-remove-reading-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input value={r.location} onChange={e => updateReading(i, { location: e.target.value })}
                       placeholder="Location" className="h-10 text-xs" data-testid={`step-7-r-loc-${i}`} />
                <Select value={r.material} onValueChange={v => updateReading(i, { material: v })}>
                  <SelectTrigger className="h-10 text-xs" data-testid={`step-7-r-mat-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{MATERIALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <p className="text-[9px] text-zinc-500">Reading %</p>
                  <Input type="number" value={r.moisture_pct} onChange={e => updateReading(i, { moisture_pct: parseFloat(e.target.value) || 0 })}
                         className="h-10 text-xs" data-testid={`step-7-r-pct-${i}`} />
                </div>
                <div>
                  <p className="text-[9px] text-zinc-500">Dry Standard %</p>
                  <Input type="number" value={r.dry_standard_pct} onChange={e => updateReading(i, { dry_standard_pct: parseFloat(e.target.value) || 0 })}
                         className="h-10 text-xs" data-testid={`step-7-r-dry-${i}`} />
                </div>
                <div>
                  <p className="text-[9px] text-zinc-500">Dry vs Remove</p>
                  <Select value={r.assessment} onValueChange={v => updateReading(i, { assessment: v })}>
                    <SelectTrigger className="h-10 text-xs" data-testid={`step-7-r-asmt-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ASSESS_OPTS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      {/* Equipment & drying */}
      <Card><CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Equipment & Drying Estimate</Label>
          <Button size="sm" variant="outline" onClick={calcEquipment} data-testid="step-7-calc">
            <Calculator className="w-3.5 h-3.5 mr-1" /> Auto-calc
          </Button>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500">Affected sqft (for calculator)</p>
          <Input type="number" value={sqft} onChange={e => setSqft(parseFloat(e.target.value) || 0)}
                 className="h-10 text-xs" placeholder="e.g. 320" data-testid="step-7-sqft" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <NumberField label="Dehumidifiers" value={equipment.dehumidifiers}
                       onChange={n => setEquipment(e => ({ ...e, dehumidifiers: n }))} testId="step-7-equip-dehum" />
          <NumberField label="Air Movers" value={equipment.air_movers}
                       onChange={n => setEquipment(e => ({ ...e, air_movers: n }))} testId="step-7-equip-mover" />
          <NumberField label="Air Scrubbers" value={equipment.air_scrubbers}
                       onChange={n => setEquipment(e => ({ ...e, air_scrubbers: n }))} testId="step-7-equip-scrub" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <NumberField label="Drying days (low)" value={drying.low} step={0.5}
                       onChange={n => setDrying(d => ({ ...d, low: n }))} testId="step-7-drying-low" />
          <NumberField label="Drying days (high)" value={drying.high} step={0.5}
                       onChange={n => setDrying(d => ({ ...d, high: n }))} testId="step-7-drying-high" />
        </div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)"
                  className="w-full p-2 text-xs border border-zinc-200 rounded h-20" data-testid="step-7-notes" />
      </CardContent></Card>

      {/* Thermal imaging */}
      <Card><CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-2">
          <Thermometer className="w-4 h-4 text-rose-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Thermal Imaging (Optional)</Label>
        </div>
        <p className="text-[10px] text-zinc-500 mb-2 italic">
          Thermal imaging not available on this device. You can upload thermal images from your camera roll if captured on a separate device.
        </p>
        <input ref={thermalRef} type="file" accept="image/*,.heic,.heif" className="hidden"
               onChange={uploadThermal} data-testid="step-7-thermal-input" />
        <Button variant="outline" onClick={() => openPhotoPicker(thermalRef)} disabled={uploadingThermal}
                className="w-full h-11" data-testid="step-7-thermal-upload-btn">
          {uploadingThermal ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading & analyzing…</>
            : <><Upload className="w-4 h-4 mr-1" /> Upload Thermal Image from Camera Roll</>}
        </Button>

        <div className="mt-3 space-y-2">
          {thermals.map(img => (
            <div key={img.id} className="border border-rose-100 rounded-lg overflow-hidden bg-rose-50/40"
                 data-testid={`step-7-thermal-${img.id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`} alt="thermal"
                   className="w-full h-40 object-cover" />
              <div className="p-2">
                {img.ai_analysis && <>
                  <p className="text-[9px] uppercase tracking-wider font-bold text-rose-700 mb-1">AI Thermal Analysis</p>
                  <p className="text-xs text-zinc-700 leading-relaxed whitespace-pre-wrap">{img.ai_analysis}</p>
                  <AIResultNote feature="thermal" refId={img.id} text={img.ai_analysis} />
                </>}
                <div className="flex gap-2 mt-2">
                  {!img.accepted ? (
                    <Button size="sm" className="flex-1 bg-emerald-600 text-white" onClick={() => acceptThermal(img)}
                            data-testid={`step-7-thermal-accept-${img.id}`}>
                      <Check className="w-3 h-3 mr-1" /> Accept
                    </Button>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-700"><Check className="w-3 h-3 mr-0.5" /> Accepted</Badge>
                  )}
                  <Button size="sm" variant="outline" onClick={() => deleteThermal(img.id)}
                          data-testid={`step-7-thermal-delete-${img.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent></Card>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => setShowNaDialog(true)}
                className="h-12 border-zinc-300" data-testid="step-7-na-btn">
          <AlertCircle className="w-4 h-4 mr-1" /> Not Applicable
        </Button>
        <Button onClick={save} disabled={saving}
                className="h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="step-7-complete-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Mark Complete</>}
        </Button>
      </div>

      {showNaDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4"
             data-testid="step-7-na-dialog">
          <Card className="w-full max-w-sm">
            <CardContent className="pt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-zinc-900">Mark Step 7 as Not Applicable</h3>
                <Button size="sm" variant="ghost" onClick={() => setShowNaDialog(false)}><X className="w-4 h-4" /></Button>
              </div>
              <p className="text-xs text-zinc-600">A manager or owner may later override this back to required.</p>
              <textarea value={naReason} onChange={e => setNaReason(e.target.value)}
                        placeholder="Reason (required)"
                        className="w-full p-2 text-xs border border-zinc-200 rounded h-20" data-testid="step-7-na-reason" />
              <Button onClick={markNA} className="w-full h-11 bg-zinc-900 text-white" data-testid="step-7-na-submit">
                Confirm Not Applicable
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function NumberField({ label, value, step = 1, onChange, testId }) {
  return (
    <div>
      <p className="text-[10px] text-zinc-500">{label}</p>
      <Input type="number" step={step} value={value}
             onChange={e => onChange(parseFloat(e.target.value) || 0)}
             className="h-10 text-xs" data-testid={testId} />
    </div>
  );
}
